import React, { useEffect, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';

import Axios from 'axios';
import {
  Button,
  Divider,
  FormView,
  Heading3,
  Paragraph1,
  Rules,
  SmallText,
  TextArea,
} from 'inpock-ui-react';
import { useQueryClient } from '@tanstack/react-query';

import { Box, ImageUploader, PageTitle, Rating, MediaType, Icon, OrderItem } from '@/components';
import { REVIEW_CONTENT, REVIEW_SESSION_KEY } from '@/constants';
import { useForm, useReviewQuery, useToast } from '@/hooks';
import { generateOrderlineName, thumbnail } from '@/utils';
import { api } from '@/apis';
import { keys } from '@/hooks/queries/keys';
import { REVIEW_GUIDE_MESSAGES, REVIEW_IMAGE_GUIDE_MESSAGES } from '@/constants';

import * as S from './styled';

export function ReviewModifyPage() {
  const toastOn = useToast();

  const { id } = useParams<{ id: string }>();
  const { data } = useReviewQuery(id ?? '');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const reviewInfo = sessionStorage.getItem(REVIEW_SESSION_KEY);
  const { order_group_number } = JSON.parse(reviewInfo || '{}') as { order_group_number: string };

  const { form, inputRef, register, validState } = useForm({
    rating: 0,
    medias: [],
    contents: '',
  });

  useEffect(() => {
    if (!data) return;
    const reviewProperties: (keyof typeof data)[] = ['rating', 'medias', 'contents'];
    reviewProperties.forEach((property) => {
      const value = data[property];

      if (value !== null) {
        register(property).onInput(value, property);

        if (value) {
          register(property).onComplete(value, property);
        } else {
          register(property).onInvalid(value, property);
        }
      }
    });
  }, [data]);

  const registerImage = (medias: MediaType[]) => {
    const registration = register('medias');
    registration.onInput(medias, 'medias');

    if (medias.length) registration.onComplete(medias, 'medias');
    else registration.onInvalid(medias, 'medias');
  };

  const imageHandlers = {
    add: (url: string) => {
      if (form.medias.length >= 10) return toastOn('이미지 등록 최대 개수입니다.');
      const updatedImages = [...form.medias, { url }];

      registerImage(updatedImages);
    },
    edit: (url: string, sequence: number) => {
      if (!(sequence >= 0 && sequence < form.medias.length))
        return toastOn('이미지 시퀀스가 유효하지 않아요.');
      const updatedImages: MediaType[] = [...form.medias];
      updatedImages[sequence].url = thumbnail.origin(url);

      registerImage(updatedImages);
    },
    remove: (sequence: number) => {
      const updatedImages = [...form.medias];

      if (sequence >= 0 && sequence < updatedImages.length) {
        updatedImages.splice(sequence, 1);

        registerImage(updatedImages);
      } else toastOn('이미지 시퀀스가 유효하지 않아요.');
    },
  };

  const submit = async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      await api.review.editReview(id, form);
      toastOn('상품 리뷰 수정 완료 🙌🏻');
      navigate(`/customer/orders/${order_group_number}`, { replace: true });
      queryClient.invalidateQueries(keys.orders.all);
      queryClient.invalidateQueries(keys.review.get(id));
      sessionStorage.removeItem(REVIEW_SESSION_KEY);
    } catch (error) {
      if (Axios.isAxiosError(error)) toastOn(error.response?.data.message);
      else toastOn('리뷰 수정 중, 문제가 발생했어요 😢');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <HelmetProvider>
        <Helmet>
          <title>리뷰 확인 | 인포크링크</title>
        </Helmet>
      </HelmetProvider>

      <FormView
        footer={
          <S.BottomFixedCTA>
            <Button
              fullWidth
              size="large"
              type="primary"
              onClick={submit}
              disabled={!validState.contents || !validState.rating}
              loading={submitting}
            >
              수정 완료
            </Button>
          </S.BottomFixedCTA>
        }
      >
        <div className="container">
          <PageTitle
            title="리뷰 확인"
            onClick={() => navigate(`/customer/orders/${order_group_number}`)}
            borderBottom
          />
          {data && (
            <section className="mt-20">
              <OrderItem
                image={data.image.image}
                campaignName={data.campaign_name}
                orderlineName={generateOrderlineName(
                  data.additional_product_name,
                  data.additional_option_name,
                  data.main_option_name,
                  data.sub_option_name,
                  data.quantity,
                )}
                price={data.unit_price * data.quantity}
              />
            </section>
          )}
          <Divider wide spacing={[20, 30]} />
          {/* 별점 입력 section */}
          <S.RatingSection>
            <Box
              flex
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ marginBottom: '8px', gap: '4px' }}
            >
              <Heading3>구매하신 상품은 어떠셨나요?</Heading3>
              <Icon icon="Required" size={12} />
            </Box>

            <Rating
              rating={form.rating}
              size={40}
              gap={18}
              onClick={(v) => {
                register('rating').onInput(v, 'rating');
                register('rating').onComplete(v, 'rating');
              }}
            />
          </S.RatingSection>
          <Divider spacing={30} />

          {/* 이미지 입력 section */}
          <section className="mb-30">
            <Paragraph1 className="mb-12">
              이미지 <SmallText color="gray600">({form.medias.length}/10개)</SmallText>
            </Paragraph1>
            <ImageUploader images={form.medias} handlers={imageHandlers} />
            <S.ReviewGuideContainer className="mt-8">
              {REVIEW_IMAGE_GUIDE_MESSAGES.map((message, index) => (
                <S.ReviewGuideText key={index}>{message}</S.ReviewGuideText>
              ))}
            </S.ReviewGuideContainer>
          </section>

          {/* 상세리뷰 입력 section */}
          <S.Section>
            <Box
              flex
              justifyContent="space-between"
              alignItems="center"
              sx={{ marginBottom: '4px' }}
            >
              <Box flex justifyContent="space-between" alignItems="center" sx={{ gap: '6px' }}>
                <Paragraph1>리뷰 작성</Paragraph1>
                <Icon icon="Required" size={12} />
              </Box>
              <SmallText color="gray500">
                <span className="text-bold text-black">{form.contents.length}</span> / 5000
              </SmallText>
            </Box>
            <TextArea
              ref={(el) => (inputRef.current['contents'] = el)}
              width="100%"
              value={form.contents}
              {...register('contents')}
              maxLength={REVIEW_CONTENT.rules?.max_length}
              rules={REVIEW_CONTENT.rules}
              validator={(_rules, value) =>
                REVIEW_CONTENT.validator &&
                REVIEW_CONTENT.validator(REVIEW_CONTENT.rules as Rules, value)
              }
            />
            <S.ReviewGuideContainer>
              {REVIEW_GUIDE_MESSAGES.map((message, index) => (
                <S.ReviewGuideText key={index}>{message}</S.ReviewGuideText>
              ))}
            </S.ReviewGuideContainer>
          </S.Section>
        </div>
      </FormView>
    </React.Fragment>
  );
}
